import { DeleteOutlined, DownloadOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { Chart } from '@client/components/_ui/data-display/Chart';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { NumericInput } from '@client/components/_ui/data-entry/NumericInput';
import { PercentageInput } from '@client/components/_ui/data-entry/PercentageInput';
import { PhoneNumberInput } from '@client/components/_ui/data-entry/PhoneNumberInput';
import { RangePicker } from '@client/components/_ui/data-entry/RangePicker';
import { SearchInput } from '@client/components/_ui/data-entry/SearchInput';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { TimePicker } from '@client/components/_ui/data-entry/TimePicker';
import { BetaTag } from '@client/components/_ui/display/BetaTag';
import { CollapsibleCard } from '@client/components/_ui/display/CollapsibleCard';
import { CollapsiblePanel } from '@client/components/_ui/display/CollapsiblePanel';
import { Indicator } from '@client/components/_ui/display/Indicator';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { CertaintyTag } from '@client/components/_ui/display/tags/CertaintyTag';
import { ControlFrequencyTag } from '@client/components/_ui/display/tags/ControlFrequencyTag';
import { ImpactTag } from '@client/components/_ui/display/tags/ImpactTag';
import { IncidentFrequencyTag } from '@client/components/_ui/display/tags/IncidentFrequencyTag';
import { RatingTag } from '@client/components/_ui/display/tags/RatingTag';
import { SeverityTag } from '@client/components/_ui/display/tags/SeverityTag';
import { SourceTag } from '@client/components/_ui/display/tags/SourceTag';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { notification } from '@client/components/_ui/feedback/notification';
import { Progress } from '@client/components/_ui/feedback/Progress';
import { ICONS } from '@client/constants/icons';
import { CHART_COLORS, COLOR_PALETTE, tokenValues } from '@client/design-system/__generated__';
import { createFileRoute } from '@tanstack/react-router';
import {
    Alert,
    Avatar,
    Badge,
    Breadcrumb,
    Card,
    Checkbox,
    Collapse,
    Descriptions,
    Divider,
    Dropdown,
    Empty,
    Input,
    InputNumber,
    Menu,
    Pagination,
    Popconfirm,
    Popover,
    Radio,
    Rate,
    Result,
    Select,
    Skeleton,
    Slider,
    Space,
    Spin,
    Statistic,
    Steps,
    Switch,
    Table,
    Tabs,
    Tag,
    Timeline,
    Tooltip,
    Tree,
    Typography,
    Upload,
} from 'antd';
import {
    AlertTriangle,
    Bell,
    Calendar,
    Check,
    ChevronDown,
    ChevronRight,
    Clock,
    Copy,
    Download,
    Edit,
    Eye,
    File,
    FileText,
    Filter,
    FilterX,
    Folder,
    Heart,
    Home,
    Info,
    Link2 as LinkIcon,
    Lock,
    Mail,
    Menu as MenuIcon,
    MoreHorizontal,
    Plus,
    RefreshCw,
    Search,
    Settings,
    Share,
    Star,
    Trash2,
    Upload as UploadIcon,
    User,
    X,
} from 'lucide-react';
import { useState } from 'react';
import { FileIcon } from 'react-file-icon';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/component-showcase')({
    staticData: {
        breadcrumb: { title: 'Component showcase' },
        featureFlag: 'enable-debug-info',
    },
    component: ComponentShowcase,
});

function ComponentShowcase() {
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
    const [activeSection, setActiveSection] = useState<string>('buttons');
    const [collapseOpen, setCollapseOpen] = useState(true);
    const [segmentValue, setSegmentValue] = useState('option1');
    const [switchValue, setSwitchValue] = useState(true);
    const [sliderValue, setSliderValue] = useState(30);
    const [rateValue, setRateValue] = useState(3);

    const handleClick = (buttonName: string) => {
        message.success(`${buttonName} clicked!`);
    };

    const handleLoadingClick = (key: string) => {
        setLoadingStates({ ...loadingStates, [key]: true });
        setTimeout(() => {
            setLoadingStates({ ...loadingStates, [key]: false });
            message.success('Operation completed!');
        }, 2000);
    };

    const copyToClipboard = (code: string) => {
        navigator.clipboard.writeText(code);
        message.success('Code copied to clipboard!');
    };

    const CodeBlock = ({ code }: { code: string }) => (
        <div className="relative">
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
                <code>{code}</code>
            </pre>
            <Button.Icon
                icon={<Copy />}
                size="small"
                color="gray"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(code)}
            />
        </div>
    );

    const SectionCard = ({
        title,
        description,
        children,
    }: {
        title: string;
        description?: string;
        children: React.ReactNode;
    }) => (
        <Card className="mb-6">
            <Title level={3}>{title}</Title>
            {description && <Paragraph className="text-muted-foreground">{description}</Paragraph>}
            {children}
        </Card>
    );

    const sections = [
        { key: 'buttons', label: 'Buttons' },
        { key: 'tags', label: 'Tags' },
        { key: 'badges', label: 'Status Badges' },
        { key: 'indicators', label: 'Indicators' },
        { key: 'charts', label: 'Charts' },
        { key: 'icons', label: 'Icons' },
        { key: 'file-icons', label: 'File Icons' },
        { key: 'inputs', label: 'Form Inputs' },
        { key: 'antd-inputs', label: 'Ant Design Inputs' },
        { key: 'display', label: 'Display Components' },
        { key: 'feedback', label: 'Feedback' },
        { key: 'navigation', label: 'Navigation' },
        { key: 'data-display', label: 'Data Display' },
        { key: 'colors', label: 'Colour Palette' },
    ];

    // Sample data for charts
    const barChartData = [
        { category: 'Jan', value: 30, type: 'Incidents' },
        { category: 'Feb', value: 45, type: 'Incidents' },
        { category: 'Mar', value: 25, type: 'Incidents' },
        { category: 'Apr', value: 60, type: 'Incidents' },
        { category: 'Jan', value: 20, type: 'Complaints' },
        { category: 'Feb', value: 35, type: 'Complaints' },
        { category: 'Mar', value: 40, type: 'Complaints' },
        { category: 'Apr', value: 30, type: 'Complaints' },
    ];

    const lineChartData = [
        { month: 'Jan', value: 100 },
        { month: 'Feb', value: 150 },
        { month: 'Mar', value: 120 },
        { month: 'Apr', value: 180 },
        { month: 'May', value: 200 },
        { month: 'Jun', value: 170 },
    ];

    // Sample table data
    const tableData = [
        { key: '1', name: 'John Doe', age: 32, status: 'open' },
        { key: '2', name: 'Jane Smith', age: 28, status: 'closed' },
        { key: '3', name: 'Bob Johnson', age: 45, status: 'open' },
    ];

    const tableColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Age', dataIndex: 'age', key: 'age' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => <StatusBadge status={status} module="incident" />,
        },
    ];

    return (
        <PageController route={Route}>
            <div className="container mx-auto max-w-7xl p-6">
                <Title level={2}>Component Showcase</Title>
                <Paragraph className="text-base text-muted-foreground">
                    Comprehensive guide to all UI components used in the Curium application. This page serves as a
                    reference for developers to understand and implement components consistently.
                </Paragraph>

                {/* Navigation */}
                <Card className="mb-6">
                    <div className="flex flex-wrap gap-2">
                        {sections.map((section) => (
                            <Button
                                key={section.key}
                                type={activeSection === section.key ? 'primary' : 'secondary'}
                                size="small"
                                onClick={() => {
                                    setActiveSection(section.key);
                                    document.getElementById(section.key)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                {section.label}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Divider />

                {/* =========================== BUTTONS =========================== */}
                <div id="buttons">
                    <Title level={2} className="mb-4">
                        Buttons
                    </Title>

                    <SectionCard title="Button Types" description="ButtonModern supports seven types.">
                        <Space wrap className="mb-4">
                            <Button type="primary" onClick={() => handleClick('Primary')}>
                                Primary Button
                            </Button>
                            <Button type="secondary" onClick={() => handleClick('Secondary')}>
                                Secondary Button
                            </Button>
                            <Button type="tertiary" onClick={() => handleClick('Tertiary')}>
                                Tertiary Button
                            </Button>
                            <Button type="dashed" onClick={() => handleClick('Dashed')}>
                                Dashed Button
                            </Button>
                            <Button type="link" onClick={() => handleClick('Link')}>
                                Link Button
                            </Button>
                            <Button type="danger" onClick={() => handleClick('Danger')}>
                                Danger Button
                            </Button>
                            <Button type="danger-filled" onClick={() => handleClick('Danger Filled')}>
                                Danger Filled
                            </Button>
                        </Space>
                        <CodeBlock
                            code={`<Button type="primary">Primary</Button>
<Button type="secondary">Secondary</Button>
<Button type="tertiary">Tertiary</Button>
<Button type="dashed">Dashed</Button>  // prefer Button.Add variant="dashed" for real add actions
<Button type="link">Link</Button>
<Button type="danger">Danger</Button>
<Button type="danger-filled">Danger Filled</Button>`}
                        />
                    </SectionCard>

                    <SectionCard
                        title="Button.Icon Colours"
                        description="Button.Icon supports 10 semantic colours for icon buttons."
                    >
                        <Space wrap className="mb-4">
                            <Button.Icon icon={<Star />} color="accent" tooltip="Accent" />
                            <Button.Icon icon={<Star />} color="red" tooltip="Red" />
                            <Button.Icon icon={<Star />} color="orange" tooltip="Orange" />
                            <Button.Icon icon={<Star />} color="gold" tooltip="Gold" />
                            <Button.Icon icon={<Star />} color="green" tooltip="Green" />
                            <Button.Icon icon={<Star />} color="cyan" tooltip="Cyan" />
                            <Button.Icon icon={<Star />} color="blue" tooltip="Blue" />
                            <Button.Icon icon={<Star />} color="purple" tooltip="Purple" />
                            <Button.Icon icon={<Star />} color="magenta" tooltip="Magenta" />
                            <Button.Icon icon={<Star />} color="gray" tooltip="Gray" />
                        </Space>
                        <CodeBlock
                            code={`<Button.Icon icon={<Star />} color="accent" tooltip="Accent" />
<Button.Icon icon={<Star />} color="red" tooltip="Red" />
<Button.Icon icon={<Star />} color="blue" tooltip="Blue" />`}
                        />
                    </SectionCard>

                    <SectionCard title="Danger Buttons" description="Used for destructive actions like deletion.">
                        <Space wrap className="mb-4">
                            <Button type="danger">Danger (outlined)</Button>
                            <Button type="danger-filled">Danger Filled</Button>
                        </Space>
                        <Text className="mb-2 block text-muted-foreground text-sm">
                            Use Button.Delete for table row deletions with confirmation:
                        </Text>
                        <Space wrap className="mb-4">
                            <Button.Delete
                                onConfirm={(cb) => {
                                    cb();
                                    message.success('Deleted!');
                                }}
                            />
                            <Button.Icon icon={<Trash2 />} color="red" tooltip="Custom delete icon" />
                        </Space>
                        <CodeBlock
                            code={`<Button type="danger">Danger (outlined)</Button>
<Button type="danger-filled">Danger Filled</Button>
<Button.Delete onConfirm={(cb) => { deleteItem(); cb(); }} />
<Button.Icon icon={<Trash2 />} color="red" tooltip="Delete" />`}
                        />
                    </SectionCard>

                    <SectionCard title="Button Sizes" description="Buttons come in three sizes.">
                        <Space wrap align="center" className="mb-4">
                            <Button type="primary" size="large">
                                Large
                            </Button>
                            <Button type="primary">Middle</Button>
                            <Button type="primary" size="small">
                                Small
                            </Button>
                        </Space>
                    </SectionCard>

                    <SectionCard title="Button States" description="Loading and disabled states.">
                        <Space wrap className="mb-4">
                            <Button type="primary" loading>
                                Loading
                            </Button>
                            <Button
                                type="primary"
                                loading={loadingStates['async']}
                                onClick={() => handleLoadingClick('async')}
                            >
                                Click to Load
                            </Button>
                            <Button type="primary" disabled>
                                Disabled Primary
                            </Button>
                            <Button type="secondary" disabled>
                                Disabled Secondary
                            </Button>
                        </Space>
                    </SectionCard>

                    <SectionCard title="Buttons with Icons" description="Icons can be from Ant Design or Lucide React.">
                        <Space wrap className="mb-4">
                            <Button type="primary" icon={<PlusOutlined />}>
                                Add New
                            </Button>
                            <Button type="secondary" icon={<SearchOutlined />}>
                                Search
                            </Button>
                            <Button type="tertiary" icon={<UploadOutlined />}>
                                Upload
                            </Button>
                            <Button type="secondary" icon={<DownloadOutlined />}>
                                Download
                            </Button>
                            <Button type="danger" icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Space>
                        <div className="mb-4">
                            <Text className="mb-2 block text-muted-foreground text-sm">Lucide React Icons:</Text>
                            <Space wrap>
                                <Button type="secondary" icon={<Plus className="h-4 w-4" />}>
                                    Add
                                </Button>
                                <Button type="secondary" icon={<RefreshCw className="h-4 w-4" />}>
                                    Refresh
                                </Button>
                                <Button type="secondary" icon={<FilterX className="h-4 w-4" />}>
                                    Clear
                                </Button>
                                <Button type="secondary" icon={<FileText className="h-4 w-4" />}>
                                    Documents
                                </Button>
                            </Space>
                        </div>
                    </SectionCard>

                    <SectionCard
                        title="Icon-only Buttons"
                        description="Button.Icon for coloured icon buttons in tables and toolbars."
                    >
                        <Space wrap className="mb-4">
                            <Button.Icon icon={<Edit />} color="orange" tooltip="Edit" />
                            <Button.Icon icon={<Trash2 />} color="red" tooltip="Delete" />
                            <Button.Icon icon={<Settings />} color="gray" tooltip="Settings" />
                            <Button.Icon icon={<RefreshCw />} color="cyan" tooltip="Refresh" />
                            <Button.Icon icon={<Filter />} color="blue" tooltip="Filter" size="small" />
                            <Button.Icon icon={<Eye />} color="blue" tooltip="View" />
                        </Space>
                        <CodeBlock
                            code={`<Button.Icon icon={<Edit />} color="orange" tooltip="Edit" />
<Button.Icon icon={<Trash2 />} color="red" tooltip="Delete" />
<Button.Icon icon={<RefreshCw />} color="cyan" tooltip="Refresh" />`}
                        />
                    </SectionCard>

                    <SectionCard title="Custom Button Components" description="Button.Edit and Button.Delete.">
                        <Space wrap className="mb-4">
                            <Button.Edit onClick={() => message.info('Edit clicked')} />
                            <Button.Delete
                                onConfirm={(onSuccess) => {
                                    setTimeout(() => onSuccess(), 1000);
                                }}
                            />
                            <Button.Delete disabled tooltip="Cannot delete" onConfirm={() => {}} />
                        </Space>
                        <CodeBlock
                            code={`<Button.Edit onClick={handleEdit} />
<Button.Delete onConfirm={(onSuccess) => {
    deleteItem().then(onSuccess);
}} />`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== TAGS =========================== */}
                <div id="tags">
                    <Title level={2} className="mb-4">
                        Tags (Classification)
                    </Title>
                    <Paragraph className="mb-4 text-muted-foreground">
                        Tags represent categorisation, classification, and attributes. Use tags when displaying what
                        something IS.
                    </Paragraph>

                    <SectionCard title="Severity Tags" description="Classification of severity levels.">
                        <Space wrap className="mb-4">
                            <SeverityTag value="low" />
                            <SeverityTag value="medium" />
                            <SeverityTag value="high" />
                        </Space>
                        <CodeBlock
                            code={`import { SeverityTag } from '@client/components/_ui/display/tags/SeverityTag';

<SeverityTag value="high" />
<SeverityTag value={incident.severity} />`}
                        />
                    </SectionCard>

                    <SectionCard title="Certainty Tags" description="Classification of certainty levels.">
                        <Space wrap className="mb-4">
                            <CertaintyTag value="LOW" />
                            <CertaintyTag value="MEDIUM" />
                            <CertaintyTag value="HIGH" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Impact Tags" description="Classification of impact levels.">
                        <Space wrap className="mb-4">
                            <ImpactTag value="low" />
                            <ImpactTag value="medium" />
                            <ImpactTag value="high" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Rating Tags" description="Control effectiveness ratings.">
                        <Space wrap className="mb-4">
                            <RatingTag value="EFFECTIVE" />
                            <RatingTag value="NEEDS_IMPROVEMENT" />
                            <RatingTag value="NOT_EFFECTIVE" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Control Frequency Tags" description="How often controls are executed.">
                        <Space wrap className="mb-4">
                            <ControlFrequencyTag value="DAILY" />
                            <ControlFrequencyTag value="WEEKLY" />
                            <ControlFrequencyTag value="MONTHLY" />
                            <ControlFrequencyTag value="EVERY_3_MONTHS" />
                            <ControlFrequencyTag value="ANNUAL" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Incident Frequency Tags" description="Frequency of incidents.">
                        <Space wrap className="mb-4">
                            <IncidentFrequencyTag value="low" />
                            <IncidentFrequencyTag value="medium" />
                            <IncidentFrequencyTag value="high" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Source Tags" description="Data source classification.">
                        <Space wrap className="mb-4">
                            <SourceTag value="internal" />
                            <SourceTag value="external" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Other Tags" description="Miscellaneous tag components.">
                        <Space wrap className="mb-4">
                            <BetaTag />
                            <Tag color="blue">Default Tag</Tag>
                            <Tag color="green">Success</Tag>
                            <Tag color="orange">Warning</Tag>
                            <Tag color="red">Error</Tag>
                            <Tag color="purple">Purple</Tag>
                            <Tag color="cyan">Cyan</Tag>
                        </Space>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== STATUS BADGES =========================== */}
                <div id="badges">
                    <Title level={2} className="mb-4">
                        Status Badges (Workflow State)
                    </Title>
                    <Paragraph className="mb-4 text-muted-foreground">
                        Badges represent workflow state and process status. Use badges when displaying where something
                        IS in a workflow.
                    </Paragraph>

                    <SectionCard title="Incident Status" description="Workflow states for incidents.">
                        <div className="mb-4 space-y-4">
                            <div>
                                <Text className="mb-2 block text-muted-foreground text-sm">Outlined (tables):</Text>
                                <Space wrap>
                                    <StatusBadge status="open" module="incident" variant="outlined" />
                                    <StatusBadge status="closed" module="incident" variant="outlined" />
                                </Space>
                            </div>
                            <div>
                                <Text className="mb-2 block text-muted-foreground text-sm">Filled (headers):</Text>
                                <Space wrap>
                                    <StatusBadge status="open" module="incident" variant="filled" />
                                    <StatusBadge status="closed" module="incident" variant="filled" />
                                </Space>
                            </div>
                            <div>
                                <Text className="mb-2 block text-muted-foreground text-sm">Dot (minimal):</Text>
                                <Space wrap>
                                    <StatusBadge status="open" module="incident" variant="dot" />
                                    <StatusBadge status="closed" module="incident" variant="dot" />
                                </Space>
                            </div>
                        </div>
                        <CodeBlock
                            code={`import { StatusBadge } from '@client/components/_ui/display/StatusBadge';

<StatusBadge status="open" module="incident" variant="outlined" />
<StatusBadge status="open" module="incident" variant="filled" />
<StatusBadge status="open" module="incident" variant="dot" />`}
                        />
                    </SectionCard>

                    {/* <SectionCard title="Claim Status" description="Workflow states for claims.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="pending" module="claim" />
                            <StatusBadge status="open" module="claim" />
                            <StatusBadge status="closed" module="claim" />
                            <StatusBadge status="closed_in_recovery_only" module="claim" />
                            <StatusBadge status="withdrawn" module="claim" />
                            <StatusBadge status="cancelled" module="claim" />
                            <StatusBadge status="reopened" module="claim" />
                        </Space>
                    </SectionCard> */}

                    <SectionCard title="Complaint Status" description="Workflow states for complaints.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="OPEN" module="complaint" />
                            <StatusBadge status="CLOSED" module="complaint" />
                            <StatusBadge status="RE_OPENED" module="complaint" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Obligation Status" description="Workflow states for obligations.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="PUBLISHED" module="obligation" />
                            <StatusBadge status="DRAFT" module="obligation" />
                            <StatusBadge status="AWAITING_REVIEW" module="obligation" />
                            <StatusBadge status="DECOMMISSIONED" module="obligation" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Task Status" description="Workflow states for tasks.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="OVERDUE" module="task" />
                            <StatusBadge status="DUE_SOON" module="task" />
                            <StatusBadge status="NOT_DUE" module="task" />
                            <StatusBadge status="DONE" module="task" />
                            <StatusBadge status="NOT_APPLICABLE" module="task" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Message Classification Status" description="Message processing states.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="PENDING" module="messageClassification" />
                            <StatusBadge status="PROCESSED" module="messageClassification" />
                            <StatusBadge status="ERROR" module="messageClassification" />
                            <StatusBadge status="REVIEWED" module="messageClassification" />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Document Analysis Status" description="Document processing states.">
                        <Space wrap className="mb-4">
                            <StatusBadge status="PENDING" module="documentAnalysis" />
                            <StatusBadge status="EXTRACTING_TEXT" module="documentAnalysis" />
                            <StatusBadge status="ANALYZING" module="documentAnalysis" />
                            <StatusBadge status="COMPLETED" module="documentAnalysis" />
                            <StatusBadge status="FAILED" module="documentAnalysis" />
                        </Space>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== INDICATORS =========================== */}
                <div id="indicators">
                    <Title level={2} className="mb-4">
                        Indicators
                    </Title>

                    <SectionCard title="Colour Indicators" description="Simple coloured dots for status indication.">
                        <Space wrap className="mb-4">
                            <Indicator tooltip={{ title: 'Success' }} style={{ background: 'var(--color-success)' }} />
                            <Indicator tooltip={{ title: 'Warning' }} style={{ background: 'var(--color-warning)' }} />
                            <Indicator tooltip={{ title: 'Danger' }} style={{ background: 'var(--color-danger)' }} />
                            <Indicator tooltip={{ title: 'Info' }} style={{ background: 'var(--color-info)' }} />
                            <Indicator
                                tooltip={{ title: 'Primary' }}
                                style={{ background: 'var(--color-accent-500)' }}
                            />
                        </Space>
                        <CodeBlock
                            code={`import { Indicator } from '@client/components/_ui/display/Indicator';

<Indicator
    tooltip={{ title: 'Success' }}
    style={{ background: 'var(--color-success)' }}
/>`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== CHARTS =========================== */}
                <div id="charts">
                    <Title level={2} className="mb-4">
                        Charts
                    </Title>
                    <Paragraph className="mb-4 text-muted-foreground">
                        Charts are built using @antv/g2 with a custom wrapper component.
                    </Paragraph>

                    <SectionCard title="Bar Chart (Interval)" description="Stacked bar chart example.">
                        <div className="mb-4" style={{ height: 300 }}>
                            <Chart
                                height={280}
                                items={[
                                    {
                                        type: 'interval',
                                        data: barChartData,
                                        encode: {
                                            x: 'category',
                                            y: 'value',
                                            color: 'type',
                                        },
                                        transform: [{ type: 'stackY' }],
                                        scale: {
                                            color: {
                                                range: [
                                                    COLOR_PALETTE.entities.incidents,
                                                    COLOR_PALETTE.entities.complaints,
                                                ],
                                            },
                                        },
                                    },
                                ]}
                            />
                        </div>
                        <CodeBlock
                            code={`import { Chart } from '@client/components/_ui/data-display/Chart';

<Chart
    height={280}
    items={[{
        type: 'interval',
        data: chartData,
        encode: { x: 'category', y: 'value', color: 'type' },
        transform: [{ type: 'stackY' }],
    }]}
/>`}
                        />
                    </SectionCard>

                    <SectionCard title="Line Chart" description="Trend line chart example.">
                        <div className="mb-4" style={{ height: 300 }}>
                            <Chart
                                height={280}
                                items={[
                                    {
                                        type: 'line',
                                        data: lineChartData,
                                        encode: {
                                            x: 'month',
                                            y: 'value',
                                        },
                                        style: {
                                            stroke: CHART_COLORS.blue,
                                        },
                                    },
                                    {
                                        type: 'point',
                                        data: lineChartData,
                                        encode: {
                                            x: 'month',
                                            y: 'value',
                                        },
                                        style: {
                                            fill: CHART_COLORS.blue,
                                        },
                                    },
                                ]}
                            />
                        </div>
                    </SectionCard>

                    <SectionCard title="Chart Colour Palette" description="Available chart colours.">
                        <div className="mb-4 flex flex-wrap gap-4">
                            {Object.entries(CHART_COLORS).map(([name, color]) => (
                                <div key={name} className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded border" style={{ backgroundColor: color }} />
                                    <Text className="text-sm">{name}</Text>
                                </div>
                            ))}
                        </div>
                        <CodeBlock
                            code={`import { CHART_COLORS } from '@client/design-system/__generated__';

// Available: red, orange, yellow, green, blue, purple, grey, white, black`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== ICONS =========================== */}
                <div id="icons">
                    <Title level={2} className="mb-4">
                        Icons
                    </Title>

                    <SectionCard title="Domain Entity Icons" description="Icons for domain-specific entities.">
                        <div className="mb-4 grid grid-cols-4 gap-4 md:grid-cols-6 lg:grid-cols-8">
                            {Object.entries(ICONS).map(([name, Icon]) => (
                                <Tooltip key={name} title={name}>
                                    <div className="flex flex-col items-center rounded border p-3 hover:bg-muted">
                                        <Icon className="h-5 w-5 text-muted-foreground" />
                                        <Text className="mt-1 w-full truncate text-center text-xs">{name}</Text>
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                        <CodeBlock
                            code={`import { ICONS } from '@client/constants/icons';

<ICONS.risk className="size-4" />
<ICONS.incident className="size-4" />
<ICONS.control className="size-4" />`}
                        />
                    </SectionCard>

                    <SectionCard title="Lucide React Icons" description="Common icons from lucide-react library.">
                        <div className="mb-4 grid grid-cols-6 gap-3 md:grid-cols-8 lg:grid-cols-12">
                            {[
                                { name: 'Plus', icon: Plus },
                                { name: 'Edit', icon: Edit },
                                { name: 'Trash2', icon: Trash2 },
                                { name: 'Search', icon: Search },
                                { name: 'Filter', icon: Filter },
                                { name: 'Download', icon: Download },
                                { name: 'Upload', icon: UploadIcon },
                                { name: 'Settings', icon: Settings },
                                { name: 'User', icon: User },
                                { name: 'Mail', icon: Mail },
                                { name: 'Calendar', icon: Calendar },
                                { name: 'Clock', icon: Clock },
                                { name: 'Check', icon: Check },
                                { name: 'X', icon: X },
                                { name: 'Info', icon: Info },
                                { name: 'AlertTriangle', icon: AlertTriangle },
                                { name: 'Bell', icon: Bell },
                                { name: 'Heart', icon: Heart },
                                { name: 'Star', icon: Star },
                                { name: 'Eye', icon: Eye },
                                { name: 'Lock', icon: Lock },
                                { name: 'Share', icon: Share },
                                { name: 'Link', icon: LinkIcon },
                                { name: 'Home', icon: Home },
                                { name: 'Folder', icon: Folder },
                                { name: 'File', icon: File },
                                { name: 'FileText', icon: FileText },
                                { name: 'MoreHorizontal', icon: MoreHorizontal },
                                { name: 'ChevronDown', icon: ChevronDown },
                                { name: 'ChevronRight', icon: ChevronRight },
                                { name: 'Menu', icon: MenuIcon },
                                { name: 'RefreshCw', icon: RefreshCw },
                            ].map(({ name, icon: Icon }) => (
                                <Tooltip key={name} title={name}>
                                    <div className="flex flex-col items-center rounded border p-2 hover:bg-muted">
                                        <Icon className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                        <div className="mb-4">
                            <Text className="mb-2 block font-medium text-sm">Icon Sizes:</Text>
                            <Space wrap>
                                <div className="flex items-center gap-2">
                                    <Plus className="h-3.5 w-3.5" />
                                    <Text className="text-xs">14px (small)</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    <Text className="text-xs">16px (default)</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Plus className="h-5 w-5" />
                                    <Text className="text-xs">20px (medium)</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Plus className="h-6 w-6" />
                                    <Text className="text-xs">24px (large)</Text>
                                </div>
                            </Space>
                        </div>
                        <CodeBlock
                            code={`import { Plus, Edit, Trash2 } from 'lucide-react';

<Plus className="h-4 w-4" />  // 16px default
<Edit className="h-3.5 w-3.5" />  // 14px small
<Trash2 className="h-5 w-5" />  // 20px medium`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== FILE ICONS =========================== */}
                <div id="file-icons">
                    <Title level={2} className="mb-4">
                        File Type Icons
                    </Title>

                    <SectionCard
                        title="Document Library File Icons"
                        description="File type icons used in the Document Library."
                    >
                        <div className="mb-4 grid grid-cols-6 gap-4 md:grid-cols-8 lg:grid-cols-12">
                            {[
                                'pdf',
                                'doc',
                                'docx',
                                'xls',
                                'xlsx',
                                'ppt',
                                'pptx',
                                'txt',
                                'csv',
                                'json',
                                'xml',
                                'html',
                                'css',
                                'js',
                                'ts',
                                'tsx',
                                'md',
                                'zip',
                                'rar',
                                'png',
                                'jpg',
                                'jpeg',
                                'gif',
                                'svg',
                                'mp4',
                                'mp3',
                                'wav',
                            ].map((ext) => (
                                <Tooltip key={ext} title={`.${ext}`}>
                                    <div className="flex flex-col items-center rounded border p-2 hover:bg-muted">
                                        <div className="w-8">
                                            <FileIcon extension={ext} color="#b1359b" />
                                        </div>
                                        <Text className="mt-1 text-xs">.{ext}</Text>
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                        <CodeBlock
                            code={`import { FileIcon } from 'react-file-icon';
import { theme } from 'antd';

const { token } = theme.useToken();
<FileIcon extension="pdf" color={token.colorPrimary} />`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== FORM INPUTS =========================== */}
                <div id="inputs">
                    <Title level={2} className="mb-4">
                        Custom Form Inputs
                    </Title>

                    <SectionCard title="Date & Time Pickers" description="Custom date and time input components.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">DatePicker</Text>
                                <DatePicker className="w-full" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">RangePicker</Text>
                                <RangePicker className="w-full" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">TimePicker</Text>
                                <TimePicker className="w-full" />
                            </div>
                        </div>
                        <CodeBlock
                            code={`import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { RangePicker } from '@client/components/_ui/data-entry/RangePicker';
import { TimePicker } from '@client/components/_ui/data-entry/TimePicker';

<DatePicker />
<RangePicker />
<TimePicker />`}
                        />
                    </SectionCard>

                    <SectionCard title="Number Inputs" description="Specialised number input components.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">NumericInput</Text>
                                <NumericInput valueType="number" placeholder="Enter number" className="w-full" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">CurrencyInput</Text>
                                <CurrencyInput className="w-full" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">PercentageInput</Text>
                                <PercentageInput className="w-full" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">PhoneNumberInput</Text>
                                <PhoneNumberInput />
                            </div>
                        </div>
                        <CodeBlock
                            code={`import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { PercentageInput } from '@client/components/_ui/data-entry/PercentageInput';

<CurrencyInput placeholder="0.00" />
<PercentageInput placeholder="0%" />`}
                        />
                    </SectionCard>

                    <SectionCard title="Search Input" description="Search input with clear functionality.">
                        <div className="mb-4 max-w-md">
                            <SearchInput
                                onSearch={(value) => message.info(`Searching: ${value}`)}
                                navigateOnSearch={false}
                            />
                        </div>
                    </SectionCard>

                    <SectionCard title="Segmented Control" description="Radio-like button group.">
                        <div className="mb-4">
                            <SegmentedControl
                                value={segmentValue}
                                onChange={setSegmentValue}
                                options={[
                                    { label: 'Option 1', value: 'option1' },
                                    { label: 'Option 2', value: 'option2' },
                                    { label: 'Option 3', value: 'option3' },
                                ]}
                            />
                        </div>
                        <CodeBlock
                            code={`import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';

<SegmentedControl
    value={value}
    onChange={setValue}
    options={[
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
    ]}
/>`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== ANT DESIGN INPUTS =========================== */}
                <div id="antd-inputs">
                    <Title level={2} className="mb-4">
                        Ant Design Form Components
                    </Title>

                    <SectionCard title="Text Inputs" description="Basic text input variations.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Input</Text>
                                <Input placeholder="Basic input" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Input with prefix</Text>
                                <Input
                                    prefix={<User className="h-4 w-4 text-subtle-foreground" />}
                                    placeholder="Username"
                                />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Input.Password</Text>
                                <Input.Password placeholder="Password" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Input.TextArea</Text>
                                <Input.TextArea rows={2} placeholder="Text area" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Input.Search</Text>
                                <Input.Search placeholder="Search" />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">InputNumber</Text>
                                <InputNumber className="w-full" placeholder="Number" />
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="Select & Dropdown" description="Selection components.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Select</Text>
                                <Select
                                    className="w-full"
                                    placeholder="Select option"
                                    options={[
                                        { label: 'Option 1', value: '1' },
                                        { label: 'Option 2', value: '2' },
                                        { label: 'Option 3', value: '3' },
                                    ]}
                                />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Select Multiple</Text>
                                <Select
                                    mode="multiple"
                                    className="w-full"
                                    placeholder="Select multiple"
                                    options={[
                                        { label: 'Tag 1', value: '1' },
                                        { label: 'Tag 2', value: '2' },
                                        { label: 'Tag 3', value: '3' },
                                    ]}
                                />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Select Tags</Text>
                                <Select mode="tags" className="w-full" placeholder="Enter tags" />
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="Checkboxes & Radios" description="Selection controls.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Checkbox</Text>
                                <Space direction="vertical">
                                    <Checkbox>Checkbox 1</Checkbox>
                                    <Checkbox>Checkbox 2</Checkbox>
                                    <Checkbox disabled>Disabled</Checkbox>
                                </Space>
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Radio</Text>
                                <Radio.Group defaultValue="a">
                                    <Space direction="vertical">
                                        <Radio value="a">Option A</Radio>
                                        <Radio value="b">Option B</Radio>
                                        <Radio value="c" disabled>
                                            Disabled
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="Switch, Slider & Rate" description="Interactive controls.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Switch</Text>
                                <Switch checked={switchValue} onChange={setSwitchValue} />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Slider</Text>
                                <Slider value={sliderValue} onChange={setSliderValue} />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Rate</Text>
                                <Rate value={rateValue} onChange={setRateValue} />
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="Upload" description="File upload components.">
                        <Space wrap className="mb-4">
                            <Upload>
                                <Button icon={<UploadOutlined />}>Click to Upload</Button>
                            </Upload>
                            <Upload.Dragger className="!w-64">
                                <p className="ant-upload-drag-icon">
                                    <UploadIcon className="h-8 w-8 text-subtle-foreground" />
                                </p>
                                <p className="ant-upload-text">Drag files here</p>
                            </Upload.Dragger>
                        </Space>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== DISPLAY COMPONENTS =========================== */}
                <div id="display">
                    <Title level={2} className="mb-4">
                        Display Components
                    </Title>

                    <SectionCard title="CollapsibleCard" description="Card with collapsible content.">
                        <div className="mb-4">
                            <CollapsibleCard
                                title="Collapsible Card Example"
                                isCollapsible
                                isDefaultExpanded
                                rightAddon={<Button size="small">Action</Button>}
                            >
                                <div className="p-4">
                                    <Paragraph>This is the card content that can be collapsed.</Paragraph>
                                </div>
                            </CollapsibleCard>
                        </div>
                        <CodeBlock
                            code={`import { CollapsibleCard } from '@client/components/_ui/display/CollapsibleCard';

<CollapsibleCard
    title="Card Title"
    isCollapsible
    isDefaultExpanded
    rightAddon={<Button size="small">Action</Button>}
>
    <p>Card content</p>
</CollapsibleCard>`}
                        />
                    </SectionCard>

                    <SectionCard title="CollapsiblePanel" description="Animated collapse panel.">
                        <div className="mb-4">
                            <Button onClick={() => setCollapseOpen(!collapseOpen)} className="mb-2">
                                Toggle Panel
                            </Button>
                            <CollapsiblePanel open={collapseOpen}>
                                <div className="rounded bg-muted p-4">
                                    <Paragraph>This content can be collapsed and expanded with animation.</Paragraph>
                                </div>
                            </CollapsiblePanel>
                        </div>
                    </SectionCard>

                    <SectionCard title="Progress" description="Progress indicators.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Line Progress</Text>
                                <Progress percent={30} />
                                <Progress percent={50} status="active" />
                                <Progress percent={70} status="exception" />
                                <Progress percent={100} />
                            </div>
                            <div>
                                <Text className="mb-2 block font-medium text-sm">Circle Progress</Text>
                                <Space>
                                    <Progress type="circle" percent={75} size={80} />
                                    <Progress type="circle" percent={100} size={80} />
                                    <Progress type="circle" percent={70} status="exception" size={80} />
                                </Space>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="Avatar & Badge" description="User avatars and notification badges.">
                        <Space wrap className="mb-4">
                            <Avatar>U</Avatar>
                            <Avatar style={{ backgroundColor: tokenValues.color.accent[500] }}>JD</Avatar>
                            <Avatar icon={<User className="h-4 w-4" />} />
                            <Badge count={5}>
                                <Avatar shape="square">A</Avatar>
                            </Badge>
                            <Badge dot>
                                <Avatar>B</Avatar>
                            </Badge>
                            <Badge count={0} showZero>
                                <Avatar>C</Avatar>
                            </Badge>
                        </Space>
                    </SectionCard>

                    <SectionCard title="Empty States" description="Placeholder for no data.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Empty />
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items found" />
                        </div>
                    </SectionCard>

                    <SectionCard title="Skeleton" description="Loading placeholder.">
                        <Skeleton active />
                    </SectionCard>

                    <SectionCard title="Spin" description="Loading spinner.">
                        <Space wrap className="mb-4">
                            <Spin size="small" />
                            <Spin />
                            <Spin size="large" />
                            <Spin tip="Loading...">
                                <div className="rounded bg-muted p-8">Content</div>
                            </Spin>
                        </Space>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== FEEDBACK =========================== */}
                <div id="feedback">
                    <Title level={2} className="mb-4">
                        Feedback Components
                    </Title>

                    <SectionCard title="Messages" description="Toast messages for feedback.">
                        <Space wrap className="mb-4">
                            <Button onClick={() => message.success('Success message!')}>Success</Button>
                            <Button onClick={() => message.error('Error message!')}>Error</Button>
                            <Button onClick={() => message.warning('Warning message!')}>Warning</Button>
                            <Button onClick={() => message.info('Info message!')}>Info</Button>
                            <Button onClick={() => message.loading('Loading...', 2)}>Loading</Button>
                        </Space>
                        <CodeBlock
                            code={`import { message } from '@client/components/_ui/feedback/message';

message.success('Operation successful!');
message.error('An error occurred!');
message.warning('Please be careful!');
message.info('Just FYI...');
message.loading('Processing...');`}
                        />
                    </SectionCard>

                    <SectionCard title="Notifications" description="Notifications with title and description.">
                        <Space wrap className="mb-4">
                            <Button
                                onClick={() =>
                                    notification.success({
                                        title: 'Success',
                                        description: 'Your changes have been saved.',
                                    })
                                }
                            >
                                Success
                            </Button>
                            <Button
                                onClick={() =>
                                    notification.error({
                                        title: 'Error',
                                        description: 'Something went wrong.',
                                    })
                                }
                            >
                                Error
                            </Button>
                            <Button
                                onClick={() =>
                                    notification.warning({
                                        title: 'Warning',
                                        description: 'Please review before continuing.',
                                    })
                                }
                            >
                                Warning
                            </Button>
                            <Button
                                onClick={() =>
                                    notification.info({
                                        title: 'Info',
                                        description: 'Here is some information.',
                                    })
                                }
                            >
                                Info
                            </Button>
                        </Space>
                        <CodeBlock
                            code={`import { notification } from '@client/components/_ui/feedback/notification';

notification.success({
    title: 'Success',
    description: 'Your changes have been saved.',
});`}
                        />
                    </SectionCard>

                    <SectionCard title="Modal" description="Modal dialogs.">
                        <Space wrap className="mb-4">
                            <Button
                                onClick={() =>
                                    modal.confirm({
                                        title: 'Confirm Action',
                                        content: 'Are you sure you want to proceed?',
                                        onOk: () => message.success('Confirmed!'),
                                    })
                                }
                            >
                                Confirm Modal
                            </Button>
                            <Button
                                onClick={() =>
                                    modal.info({
                                        title: 'Information',
                                        content: 'This is an informational modal.',
                                    })
                                }
                            >
                                Info Modal
                            </Button>
                            <Button
                                onClick={() =>
                                    modal.warning({
                                        title: 'Warning',
                                        content: 'This is a warning modal.',
                                    })
                                }
                            >
                                Warning Modal
                            </Button>
                            <Button
                                onClick={() =>
                                    modal.error({
                                        title: 'Error',
                                        content: 'This is an error modal.',
                                    })
                                }
                            >
                                Error Modal
                            </Button>
                        </Space>
                        <CodeBlock
                            code={`import { modal } from '@client/components/_ui/feedback/modal';

modal.confirm({
    title: 'Confirm Action',
    content: 'Are you sure?',
    onOk: () => handleConfirm(),
});`}
                        />
                    </SectionCard>

                    <SectionCard title="Alerts" description="Static alert messages.">
                        <div className="mb-4 space-y-2">
                            <Alert title="Success message" type="success" showIcon />
                            <Alert title="Info message" type="info" showIcon />
                            <Alert title="Warning message" type="warning" showIcon />
                            <Alert title="Error message" type="error" showIcon />
                            <Alert
                                title="With description"
                                description="This is a detailed description of the alert."
                                type="info"
                                showIcon
                            />
                        </div>
                    </SectionCard>

                    <SectionCard title="Popconfirm" description="Confirmation popover.">
                        <Space wrap className="mb-4">
                            <Popconfirm
                                title="Are you sure?"
                                onConfirm={() => message.success('Confirmed!')}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="danger">Delete</Button>
                            </Popconfirm>
                        </Space>
                    </SectionCard>

                    <SectionCard title="Result" description="Result page states.">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Result status="success" title="Success" subTitle="Order placed successfully." />
                            <Result status="error" title="Error" subTitle="Something went wrong." />
                        </div>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== NAVIGATION =========================== */}
                <div id="navigation">
                    <Title level={2} className="mb-4">
                        Navigation Components
                    </Title>

                    <SectionCard title="Breadcrumb" description="Navigation path indicator.">
                        <Breadcrumb
                            className="mb-4"
                            items={[
                                { title: <Home className="h-4 w-4" /> },
                                { title: 'Dashboard' },
                                { title: 'Settings' },
                                { title: 'Profile' },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Steps" description="Step-by-step navigation.">
                        <Steps
                            className="mb-4"
                            current={1}
                            items={[
                                { title: 'Step 1', description: 'Details' },
                                { title: 'Step 2', description: 'Review' },
                                { title: 'Step 3', description: 'Confirm' },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Pagination" description="Page navigation.">
                        <Pagination defaultCurrent={1} total={500} showSizeChanger showQuickJumper className="mb-4" />
                    </SectionCard>

                    <SectionCard title="Tabs" description="Tab navigation.">
                        <Tabs
                            className="mb-4"
                            items={[
                                { key: '1', label: 'Tab 1', children: 'Content of Tab 1' },
                                { key: '2', label: 'Tab 2', children: 'Content of Tab 2' },
                                { key: '3', label: 'Tab 3', children: 'Content of Tab 3' },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Menu" description="Navigation menu.">
                        <Menu
                            mode="horizontal"
                            className="mb-4"
                            items={[
                                { key: 'home', icon: <Home className="h-4 w-4" />, label: 'Home' },
                                { key: 'dashboard', icon: <Settings className="h-4 w-4" />, label: 'Dashboard' },
                                {
                                    key: 'settings',
                                    label: 'Settings',
                                    children: [
                                        { key: 'profile', label: 'Profile' },
                                        { key: 'account', label: 'Account' },
                                    ],
                                },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Dropdown" description="Dropdown menu.">
                        <Dropdown
                            menu={{
                                items: [
                                    { key: '1', label: 'Option 1' },
                                    { key: '2', label: 'Option 2' },
                                    { type: 'divider' },
                                    { key: '3', label: 'Option 3', danger: true },
                                ],
                            }}
                        >
                            <Button>
                                Dropdown <ChevronDown className="ml-1 h-4 w-4" />
                            </Button>
                        </Dropdown>
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== DATA DISPLAY =========================== */}
                <div id="data-display">
                    <Title level={2} className="mb-4">
                        Data Display Components
                    </Title>

                    <SectionCard title="Table" description="Data table with StatusBadge.">
                        <Table
                            columns={tableColumns}
                            dataSource={tableData}
                            size="small"
                            pagination={false}
                            className="mb-4"
                        />
                    </SectionCard>

                    <SectionCard title="Descriptions" description="Display key-value pairs.">
                        <Descriptions bordered size="small" className="mb-4">
                            <Descriptions.Item label="Name">John Doe</Descriptions.Item>
                            <Descriptions.Item label="Email">john@example.com</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <StatusBadge status="open" module="incident" />
                            </Descriptions.Item>
                        </Descriptions>
                    </SectionCard>

                    <SectionCard title="Statistic" description="Display statistics.">
                        <Space wrap className="mb-4">
                            <Statistic title="Active Users" value={1128} />
                            <Statistic title="Claims" value={93} prefix={<FileText className="h-4 w-4" />} />
                            <Statistic title="Completion" value={98.3} suffix="%" precision={1} />
                        </Space>
                    </SectionCard>

                    <SectionCard title="Timeline" description="Timeline display.">
                        <Timeline
                            className="mb-4"
                            items={[
                                { children: 'Create a project 2023-01-01' },
                                { children: 'Complete initial setup 2023-01-15', color: 'green' },
                                { children: 'Testing phase 2023-02-01', color: 'blue' },
                                { children: 'Launch 2023-03-01', color: 'red' },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Collapse" description="Collapsible content panels.">
                        <Collapse
                            className="mb-4"
                            items={[
                                { key: '1', label: 'Panel 1', children: <p>Content of panel 1</p> },
                                { key: '2', label: 'Panel 2', children: <p>Content of panel 2</p> },
                                { key: '3', label: 'Panel 3', children: <p>Content of panel 3</p> },
                            ]}
                        />
                    </SectionCard>

                    <SectionCard title="Tooltip & Popover" description="Overlay information.">
                        <Space wrap className="mb-4">
                            <Tooltip title="This is a tooltip">
                                <Button>Hover for Tooltip</Button>
                            </Tooltip>
                            <Popover title="Popover Title" content="This is the popover content.">
                                <Button>Click for Popover</Button>
                            </Popover>
                        </Space>
                    </SectionCard>

                    <SectionCard title="Tree" description="Tree structure display.">
                        <Tree
                            className="mb-4"
                            defaultExpandAll
                            treeData={[
                                {
                                    title: 'Parent 1',
                                    key: '0-0',
                                    children: [
                                        { title: 'Child 1-1', key: '0-0-0' },
                                        { title: 'Child 1-2', key: '0-0-1' },
                                    ],
                                },
                                {
                                    title: 'Parent 2',
                                    key: '0-1',
                                    children: [{ title: 'Child 2-1', key: '0-1-0' }],
                                },
                            ]}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* =========================== COLOUR PALETTE =========================== */}
                <div id="colors">
                    <Title level={2} className="mb-4">
                        Colour Palette
                    </Title>

                    <SectionCard title="Primary Accent Colours" description="Main brand colour scale.">
                        <div className="mb-4 flex flex-wrap gap-2">
                            {[25, 50, 100, 200, 300, 400, 500, 600, 700, 800].map((shade) => (
                                <div key={shade} className="text-center">
                                    <div
                                        className={`h-16 w-16 rounded border bg-accent-${shade}`}
                                        style={{ backgroundColor: `var(--color-accent-${shade})` }}
                                    />
                                    <Text className="text-xs">{shade}</Text>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    <SectionCard title="Gray Scale" description="Neutral colour scale.">
                        <div className="mb-4 flex flex-wrap gap-2">
                            {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map((shade) => (
                                <div key={shade} className="text-center">
                                    <div
                                        className="h-16 w-16 rounded border"
                                        style={{ backgroundColor: `var(--color-gray-${shade})` }}
                                    />
                                    <Text className="text-xs">{shade}</Text>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    <SectionCard title="Status Colours" description="Semantic status colours.">
                        <div className="mb-4 flex flex-wrap gap-4">
                            {[
                                { name: 'Success', var: '--color-success' },
                                { name: 'Warning', var: '--color-warning' },
                                { name: 'Danger', var: '--color-danger' },
                                { name: 'Info', var: '--color-info' },
                            ].map((color) => (
                                <div key={color.name} className="flex items-center gap-2">
                                    <div
                                        className="h-10 w-10 rounded border"
                                        style={{ backgroundColor: `var(${color.var})` }}
                                    />
                                    <Text>{color.name}</Text>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    <SectionCard title="Entity Colours" description="Domain-specific entity colours.">
                        <div className="mb-4 flex flex-wrap gap-4">
                            {Object.entries(COLOR_PALETTE.entities).map(([name, color]) => (
                                <div key={name} className="flex items-center gap-2">
                                    <div className="h-10 w-10 rounded border" style={{ backgroundColor: color }} />
                                    <Text className="capitalize">{name}</Text>
                                </div>
                            ))}
                        </div>
                        <CodeBlock
                            code={`import { COLOR_PALETTE } from '@client/design-system/__generated__';

// Available entity colours:
// complaints, incidents, obligations, controls, tests, risks, emails, tasks, claims`}
                        />
                    </SectionCard>
                </div>

                <Divider />

                {/* Best Practices */}
                <Card>
                    <Title level={3}>Best Practices</Title>
                    <div className="space-y-4">
                        <div>
                            <Text strong>1. Import Patterns:</Text>
                            <CodeBlock
                                code={`// Custom components
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { message } from '@client/components/_ui/feedback/message';
import { notification } from '@client/components/_ui/feedback/notification';
import { modal } from '@client/components/_ui/feedback/modal';

// Icons
import { ICONS } from '@client/constants/icons';
import { Plus, Edit, Trash2 } from 'lucide-react';

// Design tokens
import { CHART_COLORS, COLOR_PALETTE } from '@client/design-system/__generated__';`}
                            />
                        </div>
                        <div>
                            <Text strong>2. Tags vs Badges:</Text>
                            <Paragraph className="mb-2">
                                Use <strong>Tags</strong> for classification (severity, rating, frequency). Use{' '}
                                <strong>Badges</strong> for workflow state (open, closed, pending).
                            </Paragraph>
                        </div>
                        <div>
                            <Text strong>3. Icon Sizes:</Text>
                            <Paragraph className="mb-2">
                                Default: 16px (h-4 w-4), Small: 14px (h-3.5 w-3.5), Medium: 20px (h-5 w-5), Large: 24px
                                (h-6 w-6)
                            </Paragraph>
                        </div>
                        <div>
                            <Text strong>4. Colours:</Text>
                            <Paragraph className="mb-2">
                                Always use design system colours via CSS variables or Tailwind classes. Never hardcode
                                hex values.
                            </Paragraph>
                        </div>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
